import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { Box, Button, Checkbox, Chip, Stack, Typography } from '@mui/material';
import { grey, purple } from '@mui/material/colors';

import { ConflictResolution, RateConflictInfo } from '~/types/stopsell';

import { formatDateRange } from '~/utils/dateRangeUtils';

const PARTIAL_OVERLAP_COLORS = {
  bgcolor: purple[50],
  borderColor: purple[200],
  iconColor: purple[800],
  textColor: purple[900],
  hoverBorderColor: purple[400],
} as const;

interface Props {
  rateName: string;
  rateConflict: RateConflictInfo;
  resolution: ConflictResolution | null;
  onResolve: (resolution: ConflictResolution) => void;
  onUndo: () => void;
}

export default function StopsellRateRow({ rateName, rateConflict, resolution, onResolve, onUndo }: Props) {
  const { status, existingRange, mergedRange } = rateConflict;
  const isSkipped = resolution === 'skipped';

  const existingLabel = existingRange ? formatDateRange(existingRange.start, existingRange.end) : '';
  const mergedLabel = mergedRange ? formatDateRange(mergedRange.start, mergedRange.end) : '';

  function renderStatus() {
    if (status === 'clear') {
      return (
        <Chip
          size="small"
          label="No existing blackout"
          icon={<CheckCircleOutlineIcon />}
          sx={{
            bgcolor: 'success.50',
            color: 'success.dark',
            borderColor: 'success.200',
            fontSize: 11,
            '& .MuiChip-icon': { fontSize: 14 },
          }}
          variant="outlined"
        />
      );
    }

    if (status === 'covered_by_existing') {
      return (
        <Chip
          size="small"
          label={`Already covered by ${existingLabel}`}
          sx={{ bgcolor: grey[100], color: grey[600], borderColor: grey[300], fontSize: 11 }}
          variant="outlined"
        />
      );
    }

    if (resolution && resolution !== 'skipped') {
      const message =
        resolution === 'ignored'
          ? `Ignored (covered by ${existingLabel})`
          : resolution === 'overriding'
            ? `Will override ${existingLabel}`
            : `Will extend to ${mergedLabel}`;

      return (
        <Stack direction="row" alignItems="center" gap={1}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main' }} />
            <Typography variant="caption" color="success.dark">
              {message}
            </Typography>
          </Stack>
          <Button size="small" onClick={onUndo} sx={{ fontSize: 11, py: 0, minWidth: 0 }}>
            Undo
          </Button>
        </Stack>
      );
    }

    if (status === 'covers_existing') {
      return (
        <Stack direction="row" alignItems="center" gap={1}>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'info.main' }} />
            <Typography variant="caption" color="info.dark">
              Covers existing {existingLabel}
            </Typography>
          </Stack>
          <Stack direction="row" gap={0.5}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onResolve('ignored')}
              sx={{
                fontSize: 11,
                py: 0,
                borderColor: 'warning.400',
                color: 'warning.dark',
                '&:hover': { borderColor: 'warning.600', bgcolor: 'warning.50' },
              }}
            >
              Ignore
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onResolve('overriding')}
              sx={{
                fontSize: 11,
                py: 0,
                borderColor: 'info.200',
                color: 'info.dark',
                '&:hover': { borderColor: 'info.300', bgcolor: 'info.50' },
              }}
            >
              Override
            </Button>
          </Stack>
        </Stack>
      );
    }

    // partial_overlap
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <SyncAltIcon sx={{ fontSize: 14, color: PARTIAL_OVERLAP_COLORS.iconColor }} />
          <Typography variant="caption" sx={{ color: PARTIAL_OVERLAP_COLORS.textColor }}>
            Partial overlap with {existingLabel}
          </Typography>
        </Stack>
        <Stack direction="row" gap={0.5}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onResolve('ignored')}
            sx={{
              fontSize: 11,
              py: 0,
              borderColor: 'warning.400',
              color: 'warning.dark',
              '&:hover': { borderColor: 'warning.600', bgcolor: 'warning.50' },
            }}
          >
            Ignore
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onResolve('overriding')}
            sx={{
              fontSize: 11,
              py: 0,
              borderColor: PARTIAL_OVERLAP_COLORS.borderColor,
              color: PARTIAL_OVERLAP_COLORS.textColor,
              '&:hover': {
                borderColor: PARTIAL_OVERLAP_COLORS.hoverBorderColor,
                bgcolor: PARTIAL_OVERLAP_COLORS.bgcolor,
              },
            }}
          >
            Override
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onResolve('extending')}
            sx={{
              fontSize: 11,
              py: 0,
              borderColor: PARTIAL_OVERLAP_COLORS.borderColor,
              color: PARTIAL_OVERLAP_COLORS.textColor,
              '&:hover': {
                borderColor: PARTIAL_OVERLAP_COLORS.hoverBorderColor,
                bgcolor: PARTIAL_OVERLAP_COLORS.bgcolor,
              },
            }}
          >
            Extend to {mergedLabel}
          </Button>
        </Stack>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1,
        py: 0.5,
        borderRadius: 1,
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'grey.200',
        opacity: isSkipped ? 0.5 : 1,
      }}
    >
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ flexShrink: 0, mr: 2 }}>
        <Checkbox
          size="small"
          checked={!isSkipped}
          onChange={() => (isSkipped ? onUndo() : onResolve('skipped'))}
          sx={{ p: 0.25 }}
        />
        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          {rateName}
        </Typography>
      </Stack>
      {!isSkipped && renderStatus()}
    </Box>
  );
}
